import { useMemo } from 'react';
import { useCurrentStepRoute } from '../../components/useCurrentStepRoute';
import { FlowController } from '../FlowController';
import { useLicenseStore } from '../licenseStore';
import type { SanityLicense } from '../types/licenses';

export const useProgressValidator = (license: SanityLicense | null | undefined) => {
  const licenseStore = useLicenseStore(s => ({
    customerInformation: s.customerInformation,
    businessInformation: s.businessInformation,
    licensePlan: s.licensePlan,
    paymentInformation: s.paymentInformation,
    stepValidity: s.stepValidity,
    isHydrated: s.isHydrated,
  }));
  const currentStepRoute = useCurrentStepRoute();

  // Default state (used when license invalid). Hooks executed consistently.
  const flow = useMemo(() => {
    if (!license) return undefined;
    try {
      return new FlowController(license);
    } catch {
      return undefined;
    }
  }, [license]);

  const steps = useMemo(() => (flow ? flow.getSteps() : []), [flow]);

  const currentIndex = currentStepRoute ? steps.findIndex(s => s.stepRoute === currentStepRoute) : -1;

  const validatedSteps = useMemo(() => {
    if (!flow || !license?.licenseId) return [] as boolean[];
    return steps.map(step => (step.validateWithSelector ? step.validateWithSelector(licenseStore as never) : true));
  }, [flow, license?.licenseId, steps, licenseStore]);

  const progressSteps = steps.filter(step => !step.removeFromProgress);
  const validatedProgressSteps = steps
    .map((step, i) => ({ step, isValid: validatedSteps[i] ?? false }))
    .filter(({ step }) => !step.removeFromProgress)
    .map(({ isValid }) => isValid);

  const firstInvalidProgressIndex = validatedProgressSteps.findIndex(v => !v);
  const maxAccessibleProgressStepNumber =
    firstInvalidProgressIndex === -1 ? validatedProgressSteps.length : firstInvalidProgressIndex + 1;

  // lastValidIndex semantics changed: it is now the index of the first invalid step.
  // If every step so far is valid, it equals validatedSteps.length (i.e. number of valid steps / boundary after last valid).
  const firstInvalidIndex = validatedSteps.findIndex(v => !v);
  const lastValidIndex = firstInvalidIndex === -1 ? validatedSteps.length : firstInvalidIndex + 1;

  const lastValidStepRoute = steps[lastValidIndex - 1]?.stepRoute;
  const canAccessCurrent = currentIndex >= 0 && currentIndex < lastValidIndex;

  // Allow continue only if current step index is valid and passes validation.
  // When flow exists & store hydrated but index not found, block progression (prevents accidental true).
  const canContinue =
    flow && licenseStore.isHydrated ? currentIndex >= 0 && validatedSteps[currentIndex] === true : false;

  return {
    flow,
    steps,
    lastValidIndex,
    lastValidStepRoute,
    currentStepIndex: currentIndex,
    canAccessCurrent,
    canContinue,
    maxAccessibleProgressStepNumber,
    totalProgressSteps: progressSteps.length,
  };
};
